import {useState, useMemo, useEffect} from "react"
import {PaymentProvider, BasketTotalWithBreakDown, Address, BasketInputItem} from "@irati/types"
import {usePointsBlendQuery} from "@irati/wallet/hooks/pointsBlend"
import {isFlexiCurrency} from "@shared/wallet"
import {PointsBlendBreakdown, calculatePointsBreakdown} from "@shared/checkout/utils/basket/calculatePointsBlendBreakdown"
import basketStream, {OnError, OnComplete} from "."

interface Props {
  onError?: OnError
  onComplete?: OnComplete
}

export interface UseBasket {
  items: BasketInputItem[]
  pointBreakdown?: PointsBlendBreakdown
  paymentProvider: PaymentProvider
  breakdown: BasketTotalWithBreakDown
  isLoading: boolean
  isCashback: boolean
  shippingAddress: Address
}

export default function useBasket({onError, onComplete}: Props = {}) {
  const [basket, setBasket] = useState<UseBasket>(() => ({
    items: basketStream.items,
    paymentProvider: basketStream.paymentProvider,
    breakdown: basketStream.breakdown,
    isCashback: false,
    isLoading: false,
    shippingAddress: basketStream.shippingAddress
  }))

  useEffect(() => {
    const subscription = basketStream.subscribe(
      ({items, breakdown, isLoading, shippingAddress}) => {
        setBasket({
          paymentProvider: basketStream.paymentProvider,
          isCashback: basketStream.isCashback,
          items,
          breakdown,
          isLoading,
          shippingAddress
        })
      },
      onError,
      onComplete
    )

    return () => {
      return subscription.unsubscribe()
    }
  }, [])

  const {data: pointsBlend} = usePointsBlendQuery()

  return useMemo(() => {
    const isFlexiPurchase = isFlexiCurrency(basket.breakdown.total?.currency)
    const isPointsBlendPurchase = !!(pointsBlend?.walletBlend.visible && isFlexiPurchase)
    const blendCurrency = pointsBlend?.walletBlend.currency
    const pointBreakdown =
      pointsBlend && isFlexiPurchase
        ? calculatePointsBreakdown({
            RPWallet: pointsBlend.walletRP,
            TTWallet: pointsBlend.walletFP,
            currency: basket.breakdown.total?.currency!,
            costInCents: basket.breakdown.total?.amount!,
            isPointsBlendEligible: isPointsBlendPurchase
          })
        : undefined
    return !isPointsBlendPurchase
      ? {
          ...basket,
          pointBreakdown
        }
      : {
          ...basket,
          pointBreakdown,
          breakdown: {
            ...basket.breakdown,
            items: basket.breakdown.items?.map((item) => ({
              ...item,
              total: {
                ...item.total,
                currency: blendCurrency
              }
            })),
            total: {
              ...basket.breakdown.total,
              currency: blendCurrency
            }
          }
        }
  }, [basket, pointsBlend])
}
